import { useEffect, useRef } from 'react';
import type { ThemeSpec } from '../themes';

interface Props {
  open: boolean;
  title: string;
  body: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  theme?: ThemeSpec;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Theme-aware confirm dialog.
 *
 * - When `theme` is provided, surface/text/divider follow the active board theme so
 *   the dialog blends with classic / dark / whiteboard chromes (fixes R2-P0-3).
 * - For destructive flows the focused button is "cancel" by convention
 *   (Material/macOS/iOS) — IxD R2-P1-3.
 * - Esc closes the dialog.
 */
export default function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel = '确认',
  cancelLabel = '取消',
  destructive = false,
  theme,
  onConfirm,
  onCancel,
}: Props) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    (destructive ? cancelRef.current : confirmRef.current)?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, destructive, onCancel]);

  if (!open) return null;

  // Defaults if caller didn't pass a theme — neutral dark surface.
  const surface = theme?.surface ?? 'rgba(30,30,30,0.95)';
  const surfaceText = theme?.surfaceText ?? '#f5f5f4';
  const surfaceTextMuted = theme?.surfaceTextMuted ?? 'rgba(245,245,244,0.78)';
  const surfaceBorder = theme?.surfaceBorder ?? 'rgba(255,255,255,0.18)';
  const surfaceShadow = theme?.surfaceShadow ?? '0 16px 40px rgba(0,0,0,0.4)';
  const accent = destructive ? '#dc2626' : theme?.accent ?? '#fbbf24';
  const accentContrast = destructive ? '#fff' : theme?.accentContrast ?? '#1a1a1a';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 cursor-default z-[100] flex items-center justify-center bb-pop"
      style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)' }}
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="min-w-[320px] max-w-[420px] p-6 rounded-lg backdrop-blur-xl"
        style={{
          background: surface,
          color: surfaceText,
          border: `1px solid ${surfaceBorder}`,
          boxShadow: surfaceShadow,
        }}
      >
        <div className="text-base font-semibold mb-2.5" style={{ color: surfaceText }}>{title}</div>
        <div className="text-[13.5px] leading-relaxed" style={{ color: surfaceTextMuted }}>{body}</div>
        <div className="flex gap-2 mt-5 justify-end">
          <button
            ref={cancelRef}
            onClick={onCancel}
            className="px-4 py-2 rounded-sm text-[13px]"
            style={{
              border: `1px solid ${surfaceBorder}`,
              background: 'transparent',
              color: surfaceText,
            }}
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmRef}
            onClick={onConfirm}
            className="px-4 py-2 rounded-sm text-[13px] font-semibold"
            style={{
              border: 'none',
              background: accent,
              color: accentContrast,
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
